import React from 'react';
import PropTypes from 'prop-types';
import { get, has } from 'lodash';
import { css } from '@bd/bdui-styles/emotion';
import { Image, withSize, menuToggler } from '@bd/bdcore-utilities';
import LightBox from '@bd/bdui-lightbox';
import tc from 'tinycolor2';

import { colors } from '../styles';

const styles = css`
  padding: 0;
  margin: 0;
  position: relative;
  @media (min-width: 992px) {
    padding: 0 0 45px 0;
  }
  .Carousel_thumbnails {
    list-style: none;
    margin: 0;
    padding: 0;
    white-space: nowrap;
    overflow-x: scroll;
    scroll-behavior: smooth;
  }
  .Carousel_thumbnailItem {
    display: inline-block;
    white-space: nowrap;
    position: relative;
    margin: 0;
    height: 28vh;
    width: auto;
    background-color: transparent;
    cursor: pointer;
    overflow: hidden;
    @media(min-width: 992px) {
      height: 42vh;
      margin: 0;
      cursor: zoom-in;
    }
  }
  .Carousel_thumbnailItem-active:after, .Carousel_thumbnailItem-active:before {
    display: block;
  }
  .GalleryCarousel {
    display: flex;
    flex-direction: row;
    white-space: nowrap;
    overflow-x: scroll;
    padding: 0;
    clip-path: inset(0 0 30px 0); /* hide scroll bar */
    vertical-align: top;
    justify-content: flex-start;
    align-items: flex-start;
    position: relative;
    @media(min-width: 1600px) {
      padding: 0 0 0 80px;
    }
  }
  .GalleryCarousel_imageWrapper {
    display: inline-block;
    vertical-align: top;
    position: relative;
    margin: 0 10px 0 0;
    height: 25vh;
    @media(min-width: 992px) {
      margin: 0 30px 0 0;
      height: 40vh;
    }
    img {
      max-height: 100%;
    }
  }
  .GalleryCarousel_image {
    height: 25vh;
    width: auto;
    @media(min-width: 992px) {
      height: 40vh;
      max-height: 600px;
    }
  }
  .GalleryCarousel_imageCaption {
    position: absolute;
    bottom: 0;
    left: 0;
    background-color: ${tc(colors.brand1).setAlpha(0.8).toRgbString()};
    color: ${colors.white};
    padding: 15px;
    font-weight: 600;
  }
  .GalleryCarousel_nav {
    display: none;
    @media (min-width: 992px) {
      position: absolute;
      bottom: 0;
      left: 20px;
      display: flex;
    }
    @media (min-width: 1600px) {
      bottom: 0;
      left: 80px;
    }
  }
  .ScrollTarget {
    position: absolute;
    display: block;
    top: -200px;
  }
  .Carousel_imageButton {
    width: 60px;
    height: 60px;
    margin: 10px 10px 0 0;
    outline: 0;
    border: 0;
    background-color: ${colors.white};
    border-radius: 3px;
    background-image: url('/images/icon-arrow-right-light-blue.svg');
    background-position: center center;
    background-repeat: no-repeat;
    &-left {
      transform: scaleX(-1);
    }
    &-right {
      transform: scaleX(1);
    }
    &-disabled {
      opacity: .5;
      filter: saturate(0);
    }
  }
  .Carousel_imageElement {
    position: relative;
    margin: 0 10px 0 0;
    @media(min-width: 992px) {
      margin: 0 30px 0 0;
    }
    iframe {
      position: relative;
      width: 44vh;
      height: 25vh;
      @media(min-width: 992px) {
        height: 40vh;
        width: 70vh;
      }
    }
  }
`;

class Carousel extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      currentSlideIndex: 0,
      scrollLeft: 0,
    };
  }

  componentWillUnmount() {
    this.thumbnailListRef.removeEventListener('scroll', this.handleScroll);
  }

  setThumbnailListRef = ref => {
    if (ref && ref !== this.thumbnailListRef) {
      if (this.thumbnailListRef) {
        this.thumbnailListRef.removeEventListener('scroll', this.handleScroll);
      }
      this.thumbnailListRef = ref;
      this.thumbnailListRef.addEventListener('scroll', this.handleScroll);
    }
  }

  handleScroll = () => {
    clearTimeout(this.scrollTimer);

    this.scrollTimer = setTimeout(() => {
      this.setState({
        scrollLeft: get(this.thumbnailListRef, 'scrollLeft', 0),
      });
    }, 100);
  }

  handlePrevClick = (event = {}) => {
    if (event.preventDefault) {
      event.preventDefault();
    }
    const { currentSlideIndex } = this.state;

    if (event.which && ![13, 32].includes(event.which)) {
      return;
    }

    const prevIndex = this.findPrevElement();

    if (currentSlideIndex !== prevIndex && prevIndex !== -1) {
      this.setState({
        currentSlideIndex: prevIndex,
      }, this.scrollToActive);
    }
  }

  findPrevElement = () => {
    if (!this.thumbnailListRef) {
      return this.items.length - 1;
    }

    const { currentSlideIndex } = this.state;
    const elements = [...this.thumbnailListRef.getElementsByClassName('Carousel_thumbnailItem')];
    let nextIndex = -1;

    elements.forEach(({ offsetLeft = 0, offsetWidth: thumbWidth }, i) => {
      const scrollLeft = this.thumbnailListRef.scrollLeft;
      if (
        offsetLeft < scrollLeft
        && i < currentSlideIndex
        && i > nextIndex
      ) {
        nextIndex = i;
      }
    });

    return nextIndex;
  }

  findNextElement = () => {
    if (!this.thumbnailListRef) {
      return 0;
    }
    const { currentSlideIndex } = this.state;
    const elements = [...this.thumbnailListRef.getElementsByClassName('Carousel_thumbnailItem')];
    const nextIndex = elements.findIndex(({ offsetLeft = 0, offsetWidth: thumbWidth }, i) => {
      const containerWidth = this.thumbnailListRef.offsetWidth;
      const scrollLeft = this.thumbnailListRef.scrollLeft;
      if (
        (
          (offsetLeft + thumbWidth) > (containerWidth + scrollLeft)
        ) && i > currentSlideIndex
      ) {
        return true;
      }

      return false;
    });

    return nextIndex;
  }

  handleNextClick = (event = {}) => {
    if (event.preventDefault) {
      event.preventDefault();
    }
    const { currentSlideIndex } = this.state;

    if (event.which && ![13, 32].includes(event.which)) {
      return;
    }

    const nextIndex = this.findNextElement();

    if (currentSlideIndex !== nextIndex && nextIndex !== -1) {
      this.setState({
        currentSlideIndex: nextIndex,
      }, this.scrollToActive);
    }
  }

  scrollToActive = () => {
    if (this.thumbnailListRef) {
      const { currentSlideIndex } = this.state;
      const [{ offsetLeft = 0 }] = this.thumbnailListRef.getElementsByClassName('Carousel_thumbnailItem-active');
      this.thumbnailListRef.scrollLeft = currentSlideIndex === 0 ? 0 : offsetLeft;
    }
  }

  render() {
    const {
      items,
      className,
      thumbnailImageProps,
    } = this.props;
    const { currentSlideIndex, scrollLeft } = this.state;

    return (
      <div className={`${styles} ${className}`}>
        {
          <div>
            <LightBox items={items} enableZoom>
              <div className="GalleryCarousel">
                <ul className="Carousel_thumbnails" ref={ref => this.setThumbnailListRef(ref)}>
                  {
                    items.map((item, i) => (
                      <li key={i.toString()} data-index={i} className={`Carousel_thumbnailItem ${currentSlideIndex === i ? 'Carousel_thumbnailItem-active' : ''}`}>
                        {
                          (has(item, 'contentUrl') && (item.type !== 'video' || item.type !== 'tour')) &&
                          <span className="GalleryCarousel_imageWrapper">
                            <Image
                              src={get(item, 'contentUrl')}
                              alt={get(item, 'name', get(item, 'caption', ''))}
                              {...thumbnailImageProps}
                            />
                          </span>
                        }
                        {
                          has(item, 'element') &&
                          get(item, 'element')
                        }
                      </li>
                    ))
                  }
                </ul>
              </div>
            </LightBox>
            <div className="GalleryCarousel_nav">
              <div
                role="button"
                tabIndex="0"
                onClick={this.handlePrevClick}
                onKeyDown={this.handlePrevClick}
                className={['Carousel_imageButton', 'Carousel_imageButton-left', scrollLeft === 0 && 'Carousel_imageButton-disabled'].filter(v => v).join(' ')}
              />
              <div
                role="button"
                tabIndex="0"
                onClick={this.handleNextClick}
                onKeyDown={this.handleNextClick}
                className={['Carousel_imageButton', 'Carousel_imageButton-right', (scrollLeft + get(this.thumbnailListRef, 'offsetWidth')) >= get(this.thumbnailListRef, 'scrollWidth') && 'Carousel_imageButton-disabled'].filter(v => v).join(' ')}
              />
            </div>
          </div>
        }
      </div>
    );
  }
}

Carousel.propTypes = {
  items: PropTypes.arrayOf(PropTypes.shape({
    contentUrl: PropTypes.string,
    element: PropTypes.node,
    name: PropTypes.string,
    caption: PropTypes.string,
  })).isRequired,
  className: PropTypes.string,
  thumbnailImageProps: PropTypes.shape({
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    exact: PropTypes.bool,
    preserve: PropTypes.bool,
  }),
};

Carousel.defaultProps = {
  className: '',
  thumbnailImageProps: undefined,
};

export default menuToggler(withSize()(Carousel));



// WEBPACK FOOTER //
// ./src/components/Carousel_Elliott.js